import { useState } from "react";
import PageBanner from "@/components/PageBanner";
import Ornament from "@/components/Ornament";
import { Check, MapPin, Phone, Clock, X } from "lucide-react";

const TIMES = ["19:00", "19:30", "20:00", "20:30", "21:00", "21:30"];
const PEOPLE = ["1", "2", "3", "4", "5", "6", "7", "8+"];
const OCCASIONS = [
  "Nicio ocazie",
  "Aniversare",
  "Cerere în căsătorie",
  "Zi de naştere",
  "Întâlnire de afaceri",
  "Alt eveniment",
];

const LABEL_CLS =
  "block text-[11px] tracking-[0.3em] uppercase text-brand-gold mb-3 font-sans";
const FIELD_CLS =
  "w-full bg-transparent border-0 border-b border-brand-gold/40 focus:border-brand-gold outline-none py-3 px-1 text-brand-ivory font-sans placeholder:text-brand-ivory/30 transition-colors duration-300";

export default function Rezervari() {
  const [showSuccess, setShowSuccess] = useState(false);
  const [form, setForm] = useState({
    nume: "",
    email: "",
    telefon: "",
    data: "",
    ora: TIMES[2],
    persoane: PEOPLE[1],
    ocazie: OCCASIONS[0],
    mentiuni: "",
  });

  const update = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  const handleSubmit = (e) => {
    e.preventDefault();
    // Frontend only — show success modal (no submission)
    setShowSuccess(true);
  };

  const closeAndReset = () => {
    setShowSuccess(false);
    setForm({
      nume: "",
      email: "",
      telefon: "",
      data: "",
      ora: TIMES[2],
      persoane: PEOPLE[1],
      ocazie: OCCASIONS[0],
      mentiuni: "",
    });
  };

  return (
    <div data-testid="page-rezervari">
      <PageBanner
        overline="Réservation"
        title="Rezervaţi o Masă"
        subtitle="Vă aşteptăm să petreceţi o seară de neuitat alături de noi."
        testId="rezervari-banner"
      />

      {/* Form */}
      <section className="bg-brand-night py-16 md:py-20">
        <div className="max-w-2xl mx-auto px-6">
          <form
            data-testid="reservation-form"
            onSubmit={handleSubmit}
            className="relative bg-brand-panel border border-brand-gold/30 p-8 md:p-12"
          >
            <div className="absolute inset-3 border border-brand-gold/10 pointer-events-none" />
            <div className="relative">
              <div className="text-center mb-10">
                <span className="block text-[11px] tracking-[0.4em] uppercase text-brand-gold mb-3 font-sans">
                  Detalii rezervare
                </span>
                <h2 className="font-serif text-3xl md:text-4xl text-brand-ivory">
                  Confirmaţi <span className="italic text-brand-gold">prezenţa</span>
                </h2>
                <div className="mt-6">
                  <Ornament />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-7">
                <div className="md:col-span-2">
                  <label className={LABEL_CLS} htmlFor="nume">Nume şi prenume</label>
                  <input
                    id="nume"
                    type="text"
                    required
                    data-testid="form-nume"
                    value={form.nume}
                    onChange={(e) => update("nume", e.target.value)}
                    placeholder="Ex: Andrei Popescu"
                    className={FIELD_CLS}
                  />
                </div>

                <div>
                  <label className={LABEL_CLS} htmlFor="email">Adresă de email</label>
                  <input
                    id="email"
                    type="email"
                    required
                    data-testid="form-email"
                    value={form.email}
                    onChange={(e) => update("email", e.target.value)}
                    placeholder="nume@exemplu.ro"
                    className={FIELD_CLS}
                  />
                </div>

                <div>
                  <label className={LABEL_CLS} htmlFor="telefon">Număr de telefon</label>
                  <input
                    id="telefon"
                    type="tel"
                    required
                    data-testid="form-telefon"
                    value={form.telefon}
                    onChange={(e) => update("telefon", e.target.value)}
                    placeholder="+40 7XX XXX XXX"
                    className={FIELD_CLS}
                  />
                </div>

                <div>
                  <label className={LABEL_CLS} htmlFor="data">Data rezervării</label>
                  <input
                    id="data"
                    type="date"
                    required
                    data-testid="form-data"
                    value={form.data}
                    onChange={(e) => update("data", e.target.value)}
                    className={`${FIELD_CLS} [color-scheme:dark]`}
                  />
                </div>

                <div>
                  <label className={LABEL_CLS} htmlFor="ora">Ora rezervării</label>
                  <select
                    id="ora"
                    required
                    data-testid="form-ora"
                    value={form.ora}
                    onChange={(e) => update("ora", e.target.value)}
                    className={`${FIELD_CLS} appearance-none cursor-pointer bg-brand-panel`}
                  >
                    {TIMES.map((t) => (
                      <option key={t} value={t} className="bg-brand-panel">{t}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className={LABEL_CLS} htmlFor="persoane">Număr de persoane</label>
                  <select
                    id="persoane"
                    required
                    data-testid="form-persoane"
                    value={form.persoane}
                    onChange={(e) => update("persoane", e.target.value)}
                    className={`${FIELD_CLS} appearance-none cursor-pointer bg-brand-panel`}
                  >
                    {PEOPLE.map((p) => (
                      <option key={p} value={p} className="bg-brand-panel">{p}</option>
                    ))}
                  </select>
                </div>

                <div className="md:col-span-2">
                  <label className={LABEL_CLS} htmlFor="ocazie">Ocazie specială</label>
                  <select
                    id="ocazie"
                    data-testid="form-ocazie"
                    value={form.ocazie}
                    onChange={(e) => update("ocazie", e.target.value)}
                    className={`${FIELD_CLS} appearance-none cursor-pointer bg-brand-panel`}
                  >
                    {OCCASIONS.map((o) => (
                      <option key={o} value={o} className="bg-brand-panel">{o}</option>
                    ))}
                  </select>
                </div>

                <div className="md:col-span-2">
                  <label className={LABEL_CLS} htmlFor="mentiuni">Menţiuni speciale</label>
                  <textarea
                    id="mentiuni"
                    rows={4}
                    data-testid="form-mentiuni"
                    value={form.mentiuni}
                    onChange={(e) => update("mentiuni", e.target.value)}
                    placeholder="Alergii, preferinţe alimentare, aranjament special..."
                    className={`${FIELD_CLS} resize-none`}
                  />
                </div>
              </div>

              <button
                type="submit"
                data-testid="form-submit"
                className="mt-10 w-full bg-brand-gold text-brand-night font-sans text-[12px] tracking-[0.4em] uppercase py-4 hover:bg-brand-goldHover transition-colors duration-300"
              >
                Trimite Rezervarea
              </button>

              <p className="mt-5 text-center text-[11px] text-brand-ivory/40 font-sans tracking-wider">
                Vă vom contacta pentru confirmare în maxim 2 ore.
              </p>
            </div>
          </form>
        </div>
      </section>

      {/* Info strip */}
      <section
        data-testid="reservation-info"
        className="bg-brand-deep border-t border-brand-gold/10 py-16"
      >
        <div className="max-w-6xl mx-auto px-6 grid grid-cols-1 md:grid-cols-3 gap-10 md:gap-6 text-center">
          {[
            { Icon: Phone, label: "Rezervări telefonice", value: "+40 721 234 567" },
            {
              Icon: Clock,
              label: "Program",
              value: "Luni–Sâmbătă 12:00–23:00\nDuminică 12:00–21:00",
            },
            {
              Icon: MapPin,
              label: "Locaţie",
              value: "Strada Franceză 14\nCentrul Istoric, Bucureşti",
            },
          ].map((it, i) => {
            const Icon = it.Icon;
            return (
              <div key={i} data-testid={`info-item-${i}`} className="reveal flex flex-col items-center">
                <div className="w-14 h-14 border border-brand-gold/40 flex items-center justify-center mb-5">
                  <Icon size={22} strokeWidth={1.3} className="text-brand-gold" />
                </div>
                <span className="text-[11px] tracking-[0.35em] uppercase text-brand-gold mb-3 font-sans">
                  {it.label}
                </span>
                <p className="font-serif text-brand-ivory whitespace-pre-line leading-relaxed">
                  {it.value}
                </p>
              </div>
            );
          })}
        </div>
      </section>

      {/* Success Modal */}
      {showSuccess && (
        <div
          data-testid="success-modal"
          className="fixed inset-0 z-[70] bg-brand-night/95 backdrop-blur-sm flex items-center justify-center p-6"
          onClick={closeAndReset}
        >
          <div
            className="relative max-w-lg w-full bg-brand-panel border border-brand-gold/40 p-10 md:p-12 text-center"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="absolute inset-3 border border-brand-gold/15 pointer-events-none" />
            <button
              type="button"
              data-testid="success-modal-x"
              aria-label="Închide"
              onClick={closeAndReset}
              className="absolute top-4 right-4 text-brand-ivory/60 hover:text-brand-gold transition-colors"
            >
              <X size={20} strokeWidth={1.4} />
            </button>
            <div className="relative">
              <div className="mx-auto w-16 h-16 rounded-full border-2 border-brand-gold flex items-center justify-center mb-6 animate-fade-up">
                <Check size={28} strokeWidth={1.5} className="text-brand-gold" />
              </div>
              <span className="block text-[11px] tracking-[0.4em] uppercase text-brand-gold mb-3 font-sans">
                Confirmare
              </span>
              <h3 className="font-serif text-2xl md:text-3xl text-brand-ivory mb-5">
                Rezervarea dvs. a fost <span className="italic text-brand-gold">înregistrată</span>!
              </h3>
              <p className="font-sans text-brand-ivory/70 leading-relaxed">
                Vă vom contacta în cel mai scurt timp pentru confirmare. Vă
                mulţumim că aţi ales <span className="italic text-brand-gold">La Maison Dorée</span>.
              </p>
              <button
                type="button"
                data-testid="success-modal-close"
                onClick={closeAndReset}
                className="mt-8 inline-flex items-center justify-center px-10 py-3 border border-brand-gold text-brand-gold font-sans text-[11px] tracking-[0.35em] uppercase hover:bg-brand-gold hover:text-brand-night transition-all duration-300"
              >
                Închide
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
